/**
 * RecipeCostSummary Component
 *
 * Displays cost breakdown for a recipe based on current inventory prices.
 * Shows per-ingredient costs, totals, and warnings for missing prices.
 *
 * US-P2-010: Process Recipe Integration - Task 3
 */

import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { useProcessRecipeMutations } from '@/hooks/useProcessRecipes'
import { PER_UNIT_LABELS } from '@/hooks/useRecipeTemplates'
import {
  Calculator,
  AlertTriangle,
  Package,
  PoundSterling
} from 'lucide-react'

/**
 * Format currency value
 */
function formatCurrency(value) {
  if (value === null || value === undefined || isNaN(value)) return '-'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

/**
 * Format quantity with appropriate precision
 */
function formatQuantity(quantity) {
  if (quantity === null || quantity === undefined) return '-'
  const num = Number(quantity)
  if (num >= 1) {
    return num.toFixed(2).replace(/\.?0+$/, '')
  }
  return num.toFixed(3).replace(/\.?0+$/, '')
}

/**
 * RecipeCostSummary - Display recipe cost breakdown
 *
 * @param {string} recipeId - Recipe template ID to calculate costs for
 * @param {number} quantity - Batch size (number of blocks/bags/jars)
 * @param {boolean} showQuantityInput - Whether to show quantity input (default: true)
 * @param {function} onQuantityChange - Callback when quantity changes
 */
export default function RecipeCostSummary({
  recipeId,
  quantity = 1,
  showQuantityInput = true,
  onQuantityChange
}) {
  const { calculateRecipeCost } = useProcessRecipeMutations()

  const [costData, setCostData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [batchSize, setBatchSize] = useState(quantity)

  // Calculate costs when recipe or quantity changes
  useEffect(() => {
    if (!recipeId) {
      setCostData(null)
      return
    }

    const fetchCosts = async () => {
      setLoading(true)
      setError(null)

      try {
        const result = await calculateRecipeCost(recipeId, batchSize)
        setCostData(result)
      } catch (err) {
        console.error('[RecipeCostSummary] Error calculating costs:', err)
        setError(err.message)
        setCostData(null)
      } finally {
        setLoading(false)
      }
    }

    fetchCosts()
  }, [recipeId, batchSize, calculateRecipeCost])

  const handleQuantityChange = (value) => {
    const num = Number(value)
    if (num >= 0) {
      setBatchSize(num)
      if (onQuantityChange) {
        onQuantityChange(num)
      }
    }
  }

  if (!recipeId) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Cost Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Select a recipe to view cost breakdown
          </p>
        </CardContent>
      </Card>
    )
  }

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Cost Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-2/3" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Cost Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    )
  }

  if (!costData) {
    return null
  }

  const hasIncompletePricing = costData.missing_items && costData.missing_items.length > 0

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Calculator className="h-4 w-4" />
          Cost Summary
          {!costData.has_complete_pricing && (
            <Badge variant="outline" className="text-amber-600 border-amber-300 text-[10px]">
              Incomplete
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quantity Input */}
        {showQuantityInput && (
          <div className="flex items-center gap-3">
            <Label htmlFor="batch-size" className="text-sm shrink-0">
              Batch Size:
            </Label>
            <Input
              id="batch-size"
              type="number"
              min="1"
              value={batchSize}
              onChange={(e) => handleQuantityChange(e.target.value)}
              className="h-8 w-24"
            />
            <span className="text-sm text-muted-foreground">
              {costData.recipe_type === 'spawn' ? 'bags' :
               costData.recipe_type === 'liquid_culture' ? 'jars' : 'blocks'}
            </span>
          </div>
        )}

        {/* Missing Items Warning */}
        {hasIncompletePricing && (
          <Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-700">
              <span className="font-medium">Missing prices for:</span>
              <ul className="mt-1 ml-4 list-disc">
                {costData.missing_items.map(item => (
                  <li key={item.inventory_item_id} className="text-sm">
                    {item.item_name}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Cost Breakdown Table */}
        {costData.line_costs && costData.line_costs.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Ingredient</th>
                  <th className="text-right px-3 py-2 font-medium">Qty</th>
                  <th className="text-right px-3 py-2 font-medium">Unit Cost</th>
                  <th className="text-right px-3 py-2 font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {costData.line_costs.map((line, index) => (
                  <tr key={line.line_id || index} className="border-t">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Package className="h-3 w-3 text-muted-foreground" />
                        <span className={line.is_optional ? 'text-muted-foreground' : ''}>
                          {line.item_name}
                        </span>
                        {line.is_optional && (
                          <Badge variant="outline" className="text-[10px] h-4 px-1">
                            opt
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="text-right px-3 py-2">
                      {formatQuantity(line.quantity_required, line.unit)} {line.unit}
                    </td>
                    <td className="text-right px-3 py-2">
                      {line.has_price
                        ? formatCurrency(line.unit_cost)
                        : <span className="text-amber-600">N/A</span>
                      }
                    </td>
                    <td className="text-right px-3 py-2 font-medium">
                      {line.has_price
                        ? formatCurrency(line.line_cost)
                        : <span className="text-amber-600">-</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/30 font-medium">
                <tr className="border-t-2">
                  <td colSpan={3} className="px-3 py-2 text-right">
                    Total Material Cost:
                  </td>
                  <td className="text-right px-3 py-2">
                    <span className="flex items-center justify-end gap-1">
                      <PoundSterling className="h-3 w-3" />
                      {formatCurrency(costData.total_cost).replace('£', '')}
                    </span>
                  </td>
                </tr>
                {batchSize > 1 && (
                  <tr className="border-t text-muted-foreground">
                    <td colSpan={3} className="px-3 py-2 text-right">
                      Cost per {costData.recipe_type === 'spawn' ? 'bag' :
                                costData.recipe_type === 'liquid_culture' ? 'jar' : 'block'}:
                    </td>
                    <td className="text-right px-3 py-2">
                      {formatCurrency(costData.cost_per_unit)}
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        )}

        {/* Empty State */}
        {(!costData.line_costs || costData.line_costs.length === 0) && (
          <p className="text-sm text-muted-foreground text-center py-4">
            No ingredients defined in this recipe
          </p>
        )}
      </CardContent>
    </Card>
  )
}
